import defaultAvatar from '@assets/default-avatar.png';
import { ReceivedReviewModel } from '@company/models/ReceivedReviewModel';
import {
  ComHubHeadersEnum,
  Review,
  ReviewTypeEnum,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import DeleteRowIcon from '@components/Icons/DeleteRowIcon/DeleteRowIcon';
import { StarRating } from '@components/elements';
import DATE_FORMATS from '@constants/dateFormat';
import { formatTimeOrDateNow } from '@utils/DateUtils';
import { BASE_PATH } from '@utils/Environment';
import { format } from 'date-fns';
import {
  Button,
  Image,
  ListGroup,
  OverlayTrigger,
  Stack,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { ImNewTab } from 'react-icons/im';
import { Link } from 'react-router-dom';
import { useCompanyContext } from '@company/state/companyContext';

import './styles.css';

interface ReceivedReviewsTableProps {
  receivedReviews: Review[];
  title: string;
  onSeeReceivedReview: (review: Review) => void;
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onDismissItems?: (reviews: Review[]) => void;
  newCount?: number;
}

function ReceivedReviewsTable({
  title,
  receivedReviews,
  onShowWorkerProfile,
  onSeeReceivedReview,
  onDismissItems,
  newCount,
}: ReceivedReviewsTableProps) {
  const { indicators } = useCompanyContext();
  // Use newCount prop if provided, otherwise fall back to indicators
  const displayCount = newCount ?? indicators?.reviewsCount;
  const headers = [
    ComHubHeadersEnum.EVENT_NAME,
    ComHubHeadersEnum.WORKER_NAME,
    ComHubHeadersEnum.POSITION,
    ComHubHeadersEnum.DATES_WORKED,
    ComHubHeadersEnum.RATING,
    ComHubHeadersEnum.REPLIED,
    ComHubHeadersEnum.ACTIONS_COLUMN,
  ];

  const newReceivedReviews =
    receivedReviews?.filter(
      (receivedReview) => receivedReview.userState?.new
    ) || [];

  const handleDismissItems = (itemsToDismiss: Review[]) => {
    if (onDismissItems) {
      onDismissItems(itemsToDismiss);
    }
  };

  const handleShowWorkerProfile = (review: ReceivedReviewModel) => {
    const workerId = review.getWorkerId();
    if (onShowWorkerProfile && workerId) {
      onShowWorkerProfile({
        workerId,
        jobAssignmentId: review.jobAssignmentId,
        jobId: review.jobId,
      });
    }
  };

  return (
    <div className="th-table">
      <header className="th-table-header d-flex align-items-center flex-wrap">
        <Link
          to="?tab=received-reviews"
          className="text-decoration-none align-self-end"
          style={{ color: 'inherit' }}
        >
          <h4 className="text-start">
            {title}{' '}
            {displayCount !== undefined && (
              <b data-testid="new-counter">({displayCount})</b>
            )}
          </h4>
        </Link>
        {newReceivedReviews?.length ? (
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>Dismiss all new that&apos;s shown</Tooltip>}
          >
            <Button
              type="button"
              variant="light"
              className="ms-auto fw-bold d-flex align-items-center"
              style={{ fontSize: isMobile ? '12px' : '14px' }}
              onClick={() => handleDismissItems(newReceivedReviews)}
            >
              {newReceivedReviews.length} new
              <DeleteRowIcon className="ms-2" size={isMobile ? 18 : 22} />
            </Button>
          </OverlayTrigger>
        ) : null}
      </header>
      <hr style={{ margin: '10px 0' }} />
      {receivedReviews?.length ? (
        <>
          <BrowserView>
            <Table>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* RENDER COLUMS BY ROW */}
                {receivedReviews.map((receivedReview) => {
                  const review = new ReceivedReviewModel(receivedReview);
                  return (
                    <tr
                      key={review.getUniqueKey()}
                      className={`${review.isNew() ? 'new' : ''}`}
                    >
                      <td>
                        {review.jobId ? (
                          <Link
                            to={`${BASE_PATH}jobs/${review.jobId}`}
                            target="_blank"
                            className="cursor-point event-name-link"
                            style={{ color: 'currentcolor' }}
                          >
                            {review.eventName}
                            <ImNewTab
                              color="var(--yellow-dark-20)"
                              className="ms-2 event-name-link-icon"
                            />
                          </Link>
                        ) : (
                          <span>{review.eventName}</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleShowWorkerProfile(review)}
                          className={`${review.isNew() ? 'new' : ''}`}
                        >
                          {review.getWorkerName()}
                        </button>
                      </td>
                      <td>{review.getPosition()}</td>
                      <td>{review.getWorkedDateRange()}</td>
                      <td>{review.getRating()}</td>
                      <td>{review.hasReplied() ? 'YES' : 'NO'}</td>
                      {/* BUTTONS FOR ROW ACTIONS */}
                      <td className="actions">
                        <span className="me-3">
                          {receivedReview.companyRating &&
                            formatTimeOrDateNow(review.getDateCreated())}
                        </span>
                        <Button
                          onClick={() => onSeeReceivedReview(review)} // check if this is ok??
                          className="action-btn me-2"
                        >
                          open
                        </Button>
                        <button
                          type="button"
                          disabled={!review.isNew()}
                          style={{
                            cursor: !review.isNew() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {receivedReview.userState?.type !==
                            ReviewTypeEnum.REVIEW_LEFT && (
                            <DeleteRowIcon
                              onClick={() => handleDismissItems([review])} // check if this is ok??
                              color={!review.isNew() ? 'var(--silver)' : ''}
                            />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </BrowserView>
          <MobileView>
            <ListGroup variant="flush">
              {receivedReviews.map((receivedReview) => {
                const review = new ReceivedReviewModel(receivedReview);
                return (
                  <ListGroup.Item
                    key={review.getUniqueKey()}
                    className="ps-0 pe-0 border-0 d-flex gap-2 align-items-center"
                    onClick={(e) => {
                      const dismissButton =
                        e.currentTarget.querySelector('.dismiss-button');
                      if (!dismissButton?.contains(e.target as Node)) {
                        onSeeReceivedReview(review);
                      }
                    }}
                  >
                    <Image
                      src={
                        receivedReview.companyRating?.reviewer?.imageUrl ??
                        defaultAvatar
                      } // check if can be added to model
                      alt="reviewer profile picture"
                      width={40}
                      height={40}
                      roundedCircle
                    />
                    <Stack>
                      <span>{review.getWorkerName()}</span>
                      <Stack direction="horizontal" gap={2}>
                        <StarRating
                          value={review.getRating()!}
                          color="yellow"
                          size={15}
                        />
                        <span>{review.getRating()}</span>
                      </Stack>
                      <span>{review.getPosition()}</span>
                    </Stack>

                    <Stack className="ms-auto justify-content-around align-items-end">
                      <span>
                        {receivedReview.companyRating?.dateCreated
                          ? format(
                              new Date(
                                receivedReview.companyRating.dateCreated
                              ),
                              DATE_FORMATS.SHORT_DATE_FORMAT
                            )
                          : ''}
                      </span>
                      <Button
                        variant="light"
                        size="sm"
                        className="p-1 dismiss-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissItems([receivedReview]);
                        }} // if fails put back receivedReview
                        disabled={!review.isNew()}
                      >
                        <DeleteRowIcon
                          color={!review.isNew() ? 'var(--silver)' : ''}
                        />
                      </Button>
                    </Stack>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </MobileView>
        </>
      ) : (
        <p className="mb-2 th-fs-14">No new notifications.</p>
      )}
    </div>
  );
}

export default ReceivedReviewsTable;
